import React, { useState, useEffect } from 'react';
import { AlertTriangle, MessageSquare, TrendingDown, XOctagon, Zap, PhoneOff, Tag } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

/** Payload gravado em notifications.metadata pelo AnaliseCadastralWizard ao reprovar o termo. */
interface TermoReprovadoMeta {
    negociacao_id: number;
    titulo: string;
    cliente_nome?: string;
    decisao?: string | null;
    /** #6428: motivo principal da reprovação (rótulo) e outros que pesaram. */
    motivo?: string | null;
    motivos_outros?: string[];
    verificacao?: 'nao_confirmou' | 'numero_errado' | 'sem_resposta' | null;
    pendencias_documentais?: string[];
    pendencias_obs?: string | null;
    ressalvas?: string | null;
    divergencia_global?: { proposta: number; soma_ucs: number } | null;
    divergencias_ucs?: { uc: string; registrado: number; media_real: number; diff: number }[];
}

interface ReprovacaoNotification {
    id: string;
    content: string;
    created_at: string;
    metadata: TermoReprovadoMeta | null;
}

const VERIFICACAO_LABELS: Record<string, string> = {
    nao_confirmou: 'O cliente não confirmou a contratação na verificação telefônica.',
    numero_errado: 'A verificação telefônica não foi concluída: número de contato errado.',
    sem_resposta: 'A verificação telefônica não foi concluída: cliente sem resposta.',
};

const kwh = (v: number) => `${(v || 0).toLocaleString('pt-BR')} kWh`;

/**
 * Pop-up exibido ao abrir o site para parceiros, vendedores parceiros e
 * vendedores internos quando há termos reprovados ainda não visualizados
 * (notificações termo_reprovado não lidas).
 * Ao fechar ("Entendi"), as notificações são marcadas como lidas e o pop-up não volta;
 * o histórico permanece disponível no sino de notificações.
 */
const TermoReprovadoPopup = () => {
    const { user, profile } = useAuth();
    const [reprovacoes, setReprovacoes] = useState<ReprovacaoNotification[]>([]);
    const [closing, setClosing] = useState(false);

    const podeVerPopup = profile?.role === 'parceiro' || profile?.role === 'vendedor_parceiro' || profile?.role === 'vendedor';

    useEffect(() => {
        if (!user || !podeVerPopup) return;
        const fetchReprovacoes = async () => {
            const { data } = await supabase
                .from('notifications')
                .select('id, content, created_at, metadata')
                .eq('user_id', user.id)
                .eq('type', 'termo_reprovado')
                .eq('read', false)
                .order('created_at', { ascending: false });
            if (data && data.length > 0) setReprovacoes(data as ReprovacaoNotification[]);
        };
        fetchReprovacoes();
    }, [user, podeVerPopup]);

    // Reabertura pelo sino de notificações (NotificationDropdown dispara este evento)
    useEffect(() => {
        const handler = (e: Event) => {
            const notif = (e as CustomEvent).detail;
            if (notif?.type === 'termo_reprovado') {
                setReprovacoes([{
                    id: notif.id,
                    content: notif.content,
                    created_at: notif.created_at,
                    metadata: notif.metadata || null,
                }]);
            }
        };
        window.addEventListener('vigorhub:open-termo-reprovado', handler);
        return () => window.removeEventListener('vigorhub:open-termo-reprovado', handler);
    }, []);

    if (reprovacoes.length === 0) return null;

    const handleCiente = async () => {
        setClosing(true);
        await supabase
            .from('notifications')
            .update({ read: true })
            .in('id', reprovacoes.map(r => r.id));
        setReprovacoes([]);
        setClosing(false);
    };

    return (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
                {/* Header */}
                <div className="flex items-center gap-3 px-6 py-5 border-b border-red-200 dark:border-red-800/40 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 shrink-0">
                    <div className="p-2.5 bg-red-100 dark:bg-red-900/40 rounded-xl">
                        <XOctagon size={20} className="text-red-600" />
                    </div>
                    <div>
                        <h3 className="text-base font-bold text-red-900 dark:text-red-300">
                            {reprovacoes.length === 1 ? 'Termo reprovado na validação de dados' : `${reprovacoes.length} termos reprovados na validação de dados`}
                        </h3>
                        <p className="text-xs text-red-600 dark:text-red-400 font-medium">Revise os motivos abaixo antes de continuar.</p>
                    </div>
                </div>

                {/* Content */}
                <div className="overflow-y-auto custom-scrollbar p-6 space-y-6">
                    {reprovacoes.map(rep => {
                        const meta = rep.metadata;
                        return (
                            <div key={rep.id} className="space-y-3">
                                <div className="flex items-start justify-between gap-3">
                                    <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                                        Negociação "{meta?.titulo || meta?.cliente_nome || '—'}" foi reprovada
                                    </h4>
                                    {meta?.decisao && (
                                        <span className="shrink-0 px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-[11px] font-bold">
                                            {meta.decisao}
                                        </span>
                                    )}
                                </div>

                                {meta?.motivo && (
                                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200/60 dark:border-red-800/40">
                                        <Tag size={14} className="text-red-600 mt-0.5 shrink-0" />
                                        <p className="text-sm text-red-800 dark:text-red-300">
                                            <strong>Motivo:</strong> {meta.motivo}
                                            {meta.motivos_outros && meta.motivos_outros.length > 0 && (
                                                <span className="text-red-700/80 dark:text-red-300/80"> · também pesou: {meta.motivos_outros.join(', ')}</span>
                                            )}
                                        </p>
                                    </div>
                                )}

                                {meta?.verificacao && VERIFICACAO_LABELS[meta.verificacao] && (
                                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200/60 dark:border-red-800/40">
                                        <PhoneOff size={14} className="text-red-600 mt-0.5 shrink-0" />
                                        <p className="text-sm text-red-800 dark:text-red-300">{VERIFICACAO_LABELS[meta.verificacao]}</p>
                                    </div>
                                )}

                                {(meta?.pendencias_obs || (meta?.pendencias_documentais && meta.pendencias_documentais.length > 0)) && (
                                    <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200/60 dark:border-amber-800/40">
                                        <div className="flex items-center gap-2 mb-1">
                                            <AlertTriangle size={13} className="text-amber-600" />
                                            <span className="text-xs font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider">Pendências / Observações</span>
                                        </div>
                                        {meta.pendencias_obs && <p className="text-sm text-amber-900 dark:text-amber-200 whitespace-pre-line">{meta.pendencias_obs}</p>}
                                        {meta.pendencias_documentais && meta.pendencias_documentais.length > 0 && (
                                            <ul className="mt-1 space-y-0.5">
                                                {meta.pendencias_documentais.map((p, i) => (
                                                    <li key={i} className="text-sm text-amber-900 dark:text-amber-200">• {p}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}

                                {meta?.divergencia_global && (
                                    <div className="p-3 rounded-xl bg-orange-50 dark:bg-orange-900/10 border border-orange-200/60 dark:border-orange-800/40">
                                        <div className="flex items-center gap-2 mb-1">
                                            <AlertTriangle size={13} className="text-orange-600" />
                                            <span className="text-xs font-bold text-orange-700 dark:text-orange-400 uppercase tracking-wider">Divergência de Consumo Global</span>
                                        </div>
                                        <p className="text-sm text-orange-900 dark:text-orange-200">
                                            O consumo global preenchido na proposta ({kwh(meta.divergencia_global.proposta)}) está diferente da soma das UCs conferidas ({kwh(meta.divergencia_global.soma_ucs)}).
                                        </p>
                                    </div>
                                )}

                                {meta?.divergencias_ucs && meta.divergencias_ucs.length > 0 && meta.divergencias_ucs.map(d => (
                                    <div key={d.uc} className="p-3 rounded-xl bg-orange-50 dark:bg-orange-900/10 border border-orange-200/60 dark:border-orange-800/40">
                                        <div className="flex items-center gap-2 mb-1">
                                            <TrendingDown size={13} className="text-orange-600" />
                                            <span className="text-xs font-bold text-orange-700 dark:text-orange-400 uppercase tracking-wider">Divergência de Consumo — UC {d.uc}</span>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 text-sm text-orange-900 dark:text-orange-200">
                                            <span><strong>Registrado pelo vendedor:</strong> {kwh(d.registrado)}</span>
                                            <span className="text-orange-400">→</span>
                                            <span><strong>Média real (12 meses):</strong> {kwh(d.media_real)}</span>
                                            <span className="px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-900/40 text-[11px] font-bold flex items-center gap-1">
                                                <Zap size={10} /> {d.diff > 0 ? '+' : ''}{kwh(d.diff)}
                                            </span>
                                        </div>
                                    </div>
                                ))}

                                {meta?.ressalvas && (
                                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60">
                                        <div className="flex items-center gap-2 mb-1">
                                            <MessageSquare size={13} className="text-primary" />
                                            <span className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Ressalvas / Condições / Garantias</span>
                                        </div>
                                        <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-line">{meta.ressalvas}</p>
                                    </div>
                                )}

                                {/* Fallback: notificação antiga/sem metadata estruturado */}
                                {!meta?.motivo && !meta?.verificacao && !meta?.pendencias_obs && !(meta?.pendencias_documentais?.length) && !meta?.divergencia_global && !(meta?.divergencias_ucs?.length) && !meta?.ressalvas && (
                                    <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">{rep.content}</p>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex justify-end shrink-0 bg-gray-50/50 dark:bg-slate-900/50">
                    <button
                        onClick={handleCiente}
                        disabled={closing}
                        className="px-6 py-2.5 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary/90 transition-all disabled:opacity-60 shadow-lg shadow-primary/20"
                    >
                        {closing ? 'Salvando...' : 'Entendi'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TermoReprovadoPopup;